import React, { useState } from "react";
import styles from './.FilterBoxComponents.module.css';

const ContactLocationFilter = ({ 
  contactCities, setContactCities, 
  contactStates, setContactStates, 
  contactCountries, setContactCountries 
}) => {
  /* --- Contact Cities (No suggestions) --- */
  const [cityInput, setCityInput] = useState('');
  
  const handleCityInputChange = (event) => {
    setCityInput(event.target.value);
  };

  const handleCityInputKeyDown = (event) => {
    if (event.key === "Enter" && cityInput.trim() !== "") {
      const newCity = cityInput.trim();
      if (!contactCities.some(city => city.toLowerCase() === newCity.toLowerCase())) {
        setContactCities([...contactCities, newCity]);
      } else {
        // Replace existing city with new casing
        setContactCities(contactCities.map(city =>
          city.toLowerCase() === newCity.toLowerCase() ? newCity : city
        ));
      }
      setCityInput("");
      event.preventDefault();
    }
  };

  const removeCity = (indexToRemove) => {
    setContactCities(contactCities.filter((_, index) => index !== indexToRemove));
  };

  /* --- Contact States (With suggestions) --- */
  const [stateInput, setStateInput] = useState('');
  const [filteredStateSuggestions, setFilteredStateSuggestions] = useState([]);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  // Example suggestions for states (replace with dynamic data if needed)
  const stateSuggestions = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];
      

  const handleStateInputChange = (event) => {
    const input = event.target.value;
    setStateInput(input);
    if (input.trim() !== "") {
      const filtered = stateSuggestions.filter(state =>
        state.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredStateSuggestions(filtered);
      setShowStateDropdown(filtered.length > 0);
    } else {
      setShowStateDropdown(false);
    }
  };

  const handleStateInputKeyDown = (event) => {
    if (event.key === "Enter" && stateInput.trim() !== "") {
      const newState = stateInput.trim();
      if (!contactStates.some(state => state.toLowerCase() === newState.toLowerCase())) {
        setContactStates([...contactStates, newState]);
      } else {
        setContactStates(contactStates.map(state =>
          state.toLowerCase() === newState.toLowerCase() ? newState : state
        ));
      }
      setStateInput("");
      setShowStateDropdown(false);
      event.preventDefault();
    }
  };

  const handleSelectStateSuggestion = (state) => {
    if (!contactStates.some(s => s.toLowerCase() === state.toLowerCase())) {
      setContactStates([...contactStates, state]);
    } else {
      setContactStates(contactStates.map(s =>
        s.toLowerCase() === state.toLowerCase() ? state : s
      ));
    }
    setStateInput("");
    setShowStateDropdown(false);
  };

  const removeState = (indexToRemove) => {
    setContactStates(contactStates.filter((_, index) => index !== indexToRemove));
  };

  /* --- Contact Countries (With suggestions) --- */
  const [countryInput, setCountryInput] = useState('');
  const [filteredCountrySuggestions, setFilteredCountrySuggestions] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Example suggestions for countries (replace with dynamic data if needed)
  const countrySuggestions = ["United States","Canada","United Kingdom","Australia","Germany"
  ];

  const handleCountryInputChange = (event) => {
    const input = event.target.value;
    setCountryInput(input);
    if (input.trim() !== "") {
      const filtered = countrySuggestions.filter(country =>
        country.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredCountrySuggestions(filtered);
      setShowCountryDropdown(filtered.length > 0);
    } else {
      setShowCountryDropdown(false);
    }
  };

  const handleCountryInputKeyDown = (event) => {
    if (event.key === "Enter" && countryInput.trim() !== "") {
      const newCountry = countryInput.trim();
      if (!contactCountries.some(country => country.toLowerCase() === newCountry.toLowerCase())) {
        setContactCountries([...contactCountries, newCountry]);
      } else {
        setContactCountries(contactCountries.map(country =>
          country.toLowerCase() === newCountry.toLowerCase() ? newCountry : country
        ));
      }
      setCountryInput("");
      setShowCountryDropdown(false);
      event.preventDefault();
    }
  };

  const handleSelectCountrySuggestion = (country) => {
    if (!contactCountries.some(c => c.toLowerCase() === country.toLowerCase())) {
      setContactCountries([...contactCountries, country]);
    } else {
      setContactCountries(contactCountries.map(c =>
        c.toLowerCase() === country.toLowerCase() ? country : c
      ));
    }
    setCountryInput("");
    setShowCountryDropdown(false);
  };

  const removeCountry = (indexToRemove) => {
    setContactCountries(contactCountries.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className={styles.accordionContent}>
      {/* Contact Cities Input (No Suggestions) */}
      <label>Contact City</label>
      <input
        type="text"
        placeholder="Enter city and press ENTER..."
        value={cityInput}
        onChange={handleCityInputChange}
        onKeyDown={handleCityInputKeyDown}
        className={styles.filterInput}
      />
      <div className={styles.selectedSection}>
        {contactCities.map((city, index) => (
          <div key={index} className={styles.selectedChip}>
            {city}
            <button onClick={() => removeCity(index)} className={styles.buttonX}>
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Contact States Input (With Suggestions) */}
      <label>Contact State</label>
      <input
        type="text"
        placeholder="Enter state or select..."
        value={stateInput}
        onChange={handleStateInputChange}
        onKeyDown={handleStateInputKeyDown}
        className={styles.filterInput}
      />
      {showStateDropdown && (
        <ul className={styles.dropdown}>
          {filteredStateSuggestions.map((state, index) => (
            <li 
              key={index} 
              onClick={() => handleSelectStateSuggestion(state)} 
              className={styles.dropdownItem}
            >
              {state}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.selectedSection}>
        {contactStates.map((state, index) => (
          <div key={index} className={styles.selectedChip}>
            {state}
            <button onClick={() => removeState(index)} className={styles.buttonX}>
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Contact Countries Input (With Suggestions) */}
      <label>Contact Country</label>
      <input
        type="text"
        placeholder="Enter country or select..."
        value={countryInput}
        onChange={handleCountryInputChange}
        onKeyDown={handleCountryInputKeyDown}
        className={styles.filterInput}
      />
      {showCountryDropdown && (
        <ul className={styles.dropdown}>
          {filteredCountrySuggestions.map((country, index) => (
            <li 
              key={index} 
              onClick={() => handleSelectCountrySuggestion(country)} 
              className={styles.dropdownItem}
            >
              {country}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.selectedSection}>
        {contactCountries.map((country, index) => (
          <div key={index} className={styles.selectedChip}>
            {country}
            <button onClick={() => removeCountry(index)} className={styles.buttonX}>
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactLocationFilter;
